import React, { useEffect } from "react";
import { toFinite } from "lodash";
import {
  IconButton,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  Typography,
  makeStyles
} from "@material-ui/core";
import { Refresh, Info } from "@material-ui/icons";
import { ThemeType } from "@theme";
import locale from "@bonliva-crm/locale";
import { WithTooltip } from "@components";
import { BookingTotalsResultDto } from "@api/timereports";
import { SpinnerOverlay } from "@common";

type Props = {
  loading: boolean;
  fetch: () => void;
  data: BookingTotalsResultDto | null;
  error: boolean;
};

export const BookingFinanceSummary = (props: Props) => {
  const { data } = props;
  const classes = useStyles({});

  useEffect(() => {
    props.fetch();
  }, []);

  return (
    <Table size="small">
      <SpinnerOverlay show={props.loading && !props.data} />

      {props.error && <Typography color={"error"}>Cannot load data</Typography>}

      <TableHead>
        <TableCell>
          <div>
            Sammanställning
            <IconButton
              onClick={() => props.fetch()}
              size="small"
              style={{ marginLeft: 4 }}
            >
              <Refresh />
            </IconButton>
          </div>
        </TableCell>
        <TableCell></TableCell>
      </TableHead>

      <TableBody>
        {!props.loading && props.data && (
          <>
            <TableRow>
              <TableCell>Fakturabelopp:</TableCell>
              <TableCell align="right">
                {formatCellValue(data.totalInvoiceAmount)}
              </TableCell>
            </TableRow>

            {(data.payFormExists || data.sickFormExists) && (
              <TableRow>
                <TableCell>Sociala avgifter %:</TableCell>
                <TableCell align="right">{formatCellValue(data.socFees)}%</TableCell>
              </TableRow>
            )}

            {(data.payFormExists || props.data.sickFormExists) && (
              <TableRow>
                <TableCell>Lön inklusive sociala avgifter:</TableCell>
                <TableCell align="right">
                  {formatCellValue(data.salaryWithSocialFee, true)}
                </TableCell>
              </TableRow>
            )}

            {data.subcontractorFormExists && (
              <TableRow>
                <TableCell>Belopp för underleverantörsfaktura:</TableCell>
                <TableCell align="right">
                  {formatCellValue(data.consultantTotalWithoutVats, true)}
                </TableCell>
              </TableRow>
            )}

            {data.sickFormExists && (
              <TableRow>
                <TableCell className={classes.cellInfo}>
                  Sjuklön inklusive sociala avgifter:
                  <WithTooltip placement={"top"} title={locale.sickPaySumInfo}>
                    <Info color="disabled" className={classes.infoIcon} />
                  </WithTooltip>
                </TableCell>
                <TableCell align="right">
                  {formatCellValue(data.sickFormTotalWithSocialFees, true)}
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              <TableCell>Övrigt:</TableCell>
              <TableCell align="right">
                {formatCellValue(
                  data.subcontractorFormExists
                    ? data.totalCostMinusInvoice
                    : data.totalCostMinusSalary,
                  true
                )}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Täckningsbidrag:</TableCell>
              <TableCell align="right">{formatCellValue(data.totalProfitAbs)}</TableCell>
            </TableRow>

            <TableRow>
              <TableCell>Täckningsgrad:</TableCell>
              <TableCell align="right">{formatCellValue(data.totalProfitRel)}%</TableCell>
            </TableRow>
          </>
        )}
      </TableBody>
    </Table>
  );
};

function formatCellValue(value?: number | null, opposite = false) {
  value = value ?? 0;

  return formatString(opposite && value !== 0 ? value * -1 : value);
}

function formatString(v: number) {
  return (
    toFinite(v)
      .toLocaleString("en")
      .replace(/,/g, " ")
      .replace(/\./g, ",")
      // replace "-20" to "- 20"
      .replace(/^-/, "- ")
  );
}

const useStyles = makeStyles((theme: ThemeType) => ({
  cellInfo: {
    alignItems: "center",
    display: "flex"
  },
  infoIcon: {
    marginLeft: 5
  }
}));
